import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Bell, AlertTriangle, CreditCard, ClipboardList, CheckCheck, Loader2,
  Users, UserCog, CalendarClock, GraduationCap,
} from "lucide-react";

const CFG = {
  fee_overdue: { icon: AlertTriangle, tint: "text-red-600 bg-red-100" },
  fee_due: { icon: AlertTriangle, tint: "text-amber-600 bg-amber-100" },
  payment: { icon: CreditCard, tint: "text-accent bg-accent/10" },
  enrollment: { icon: ClipboardList, tint: "text-primary bg-primary/10" },
  batch_assigned: { icon: Users, tint: "text-sky-600 bg-sky-100" },
  trainer_assigned: { icon: UserCog, tint: "text-violet-600 bg-violet-100" },
  batch_soon: { icon: CalendarClock, tint: "text-indigo-600 bg-indigo-100" },
  course_completed: { icon: GraduationCap, tint: "text-emerald-600 bg-emerald-100" },
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 100 } });
      setItems(data.items || []);
      setUnread(data.unread_count);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openItem = async (n) => {
    if (!n.is_read) await api.post(`/notifications/${n.id}/read`);
    if (n.link) navigate(n.link); else load();
  };

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="Enrollment, batch, trainer and payment activity across your institute."
        actions={unread > 0 && (
          <Button variant="outline" size="sm" onClick={markAll} data-testid="notifications-mark-all">
            <CheckCheck className="mr-1.5 h-4 w-4" /> Mark all read
          </Button>
        )}
      />

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border text-muted-foreground" data-testid="notifications-empty">
          <Bell className="h-9 w-9" />
          <p className="text-sm">You're all caught up. 🎉</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {items.map((n) => {
            const cfg = CFG[n.type] || CFG.enrollment;
            const Icon = cfg.icon;
            return (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid={`notification-row-${n.id}`}
                className={`flex w-full items-start gap-3 border-b border-border px-4 py-3.5 text-left transition-colors last:border-0 hover:bg-slate-50 ${n.is_read ? "" : "bg-primary/[0.04]"}`}
              >
                <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${cfg.tint}`}><Icon className="h-[18px] w-[18px]" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className={`text-sm text-secondary ${n.is_read ? "font-medium" : "font-semibold"}`}>{n.title}</span>
                    {!n.is_read && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                  </span>
                  <span className="block text-sm text-muted-foreground">{n.message}</span>
                  <span className="text-[11px] text-slate-400">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
